/**
 * /compare-models - Delft3D FM, DualSPHysics and QuickFlood scored against the
 * references available at the run's site, and the model the downstream pages show.
 *
 *   1  Scores          one table, rows = models, every metric, the reference named in the header
 *   2  Decision trace  the selection step by step, with its numbers, weights and rule
 *   3  Agreement       each model's extent against the reference, cell by cell, and IoU per time step
 *   4  Selected model  the model 3D View and Impact show
 *   5  Where           the compared domain and each footprint on the satellite basemap
 *
 * Everything is GET /api/compare/models?run_id= (backend/compare/selection.py).
 * A metric the inputs cannot give is not rendered.
 */

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import type { LayerSpecification, SourceSpecification } from 'maplibre-gl'

import { api } from '../api/client'
import type { Run } from '../api/types'
import { BaseMap, type Bounds } from '../components/BaseMap'
import { C } from '../design/tokens'
import { useAppStore } from '../store/useAppStore'

type Geo = { type: string; coordinates: unknown }

interface ModelInfo {
  model: 'delft3d' | 'dualsphysics' | 'quickflood'
  solver: string
  key: string
  label: string
  source: string | null
  footprint_lonlat?: Geo
}

interface Row {
  tp: number
  fp: number
  fn: number
  matched_times?: number
  cells_compared?: number
  void_cells_excluded?: number
  iou?: number
  csi?: number
  pod?: number
  far?: number
  depth_rmse_m?: number
  depth_mae_m?: number
  depth_cells?: number
  peak_arrival_difference_s?: number
}

interface Score {
  reference: 'hecras_2d' | 'observed_sar'
  reference_label: string
  source: string | null
  rows: Record<string, Row>
  fit?: Record<string, FitRow>
  skipped: { key: string; reason: string }[]
  decision?: {
    compared: string[]
    iou_difference: number | null
    ci95: [number, number] | null
    separates: boolean
    selected: string | null
    reason?: string
    reference_wet_cells: number
    min_reference_wet_cells: number
    sample_sufficient: boolean
    outcome?: 'selected' | 'not_separated' | 'insufficient_sample'
  }
  grid?: {
    cell_m: number
    compared_cells: number
    compared_km2: number
    excluded_cells: number
    exclusion_rule: string | null
    wet_threshold_m: number
    matched_times?: number
    window_s?: [number, number]
    compared_domain_lonlat: Geo
  }
  bootstrap?: { draws: number; resampled: string; blocks: number }
  iou_series?: { times_s: number[]; by_model: Record<string, (number | null)[]> }
  classes?: { width: number; height: number; corners_lonlat: [number, number][]; by_model_u8: Record<string, string> }
}

/** One model against the reference over the domain those two share, not the domain every model shares. */
interface FitRow extends Row {
  domain_km2: number
  cell_m: number
  wet_threshold_m: number
  model_wet_threshold_m: number
  reference_wet_threshold_m: number
  window_s: [number, number]
  reference_wet_cells: number
  model_wet_cells: number
  particle_spacing_m?: number
}

interface Payload {
  run_id: string
  site_id: string
  models: ModelInfo[]
  absent_models: { model: string; solver: string; reason: string }[]
  references: {
    kind: string
    available: boolean
    label: string | null
    source: string | null
    why?: string
    footprint_lonlat?: Geo
    expected_path?: string
  }[]
  scores: Score[]
  selected_model: string | null
  weights: Record<string, number>
  decision_rule: string
}

const MODEL_COLOUR: Record<string, string> = {
  delft3d: C.modelDelft3d,
  dualsphysics: C.modelSph,
  quickflood: C.modelQuickflood,
}

const METRICS: { key: keyof Row; label: string; depth?: boolean; format: (v: number) => string }[] = [
  { key: 'iou', label: 'IoU (Jaccard)', format: (v) => v.toFixed(2) },
  { key: 'csi', label: 'Critical Success Index', format: (v) => v.toFixed(2) },
  { key: 'pod', label: 'Probability of Detection', format: (v) => v.toFixed(2) },
  { key: 'far', label: 'False Alarm Ratio', format: (v) => v.toFixed(2) },
  { key: 'depth_rmse_m', label: 'Depth RMSE (m)', depth: true, format: (v) => v.toFixed(2) },
  { key: 'depth_mae_m', label: 'Depth MAE (m)', depth: true, format: (v) => v.toFixed(2) },
  { key: 'peak_arrival_difference_s', label: 'Peak arrival time difference (h)', depth: true, format: (v) => (v / 3600).toFixed(3).replace('-', '−') },
  { key: 'matched_times', label: 'Matched time steps', depth: true, format: (v) => v.toFixed(0) },
  { key: 'cells_compared', label: 'Cells compared', format: (v) => v.toLocaleString('en-IN') },
  { key: 'void_cells_excluded', label: 'Void cells excluded', format: (v) => v.toLocaleString('en-IN') },
]

const WEIGHT_LABEL: Record<string, string> = {
  iou: 'IoU',
  csi: 'CSI',
  pod: 'POD',
  far: 'FAR',
  depth_rmse_m: 'depth RMSE',
  depth_mae_m: 'depth MAE',
  peak_arrival_difference_s: 'peak arrival difference',
}

function duration(s: number): string {
  const sign = s < 0 ? '−' : s > 0 ? '+' : ''
  const a = Math.abs(s)
  if (a < 600) return `${sign}${a.toFixed(0)} s`
  if (a < 7200) return `${sign}${(a / 60).toFixed(1)} min`
  return `${sign}${(a / 3600).toFixed(2)} h`
}

function walk(coords: unknown, out: number[]) {
  if (Array.isArray(coords) && typeof coords[0] === 'number') {
    const [x, y] = coords as number[]
    out[0] = Math.min(out[0], x)
    out[1] = Math.min(out[1], y)
    out[2] = Math.max(out[2], x)
    out[3] = Math.max(out[3], y)
  } else if (Array.isArray(coords)) coords.forEach((c) => walk(c, out))
}

function boundsOf(geoms: (Geo | undefined)[]): Bounds | null {
  const out = [Infinity, Infinity, -Infinity, -Infinity]
  geoms.forEach((g) => g && walk(g.coordinates, out))
  return Number.isFinite(out[0]) ? (out as Bounds) : null
}

const labelOf = (data: Payload, key: string) => data.models.find((m) => m.key === key)?.solver ?? key

function Section({ n, title, children, testid }: { n: number; title: string; children: React.ReactNode; testid: string }) {
  return (
    <section className="panel" data-testid={testid}>
      <header className="panel-header">
        <h2 className="panel-title">
          {n} · {title}
        </h2>
      </header>
      <div className="p-4">{children}</div>
    </section>
  )
}

function ScoreTable({ data }: { data: Payload }) {
  const groups = data.scores.map((score) => {
    const hasDepth = score.reference === 'hecras_2d'
    const cols = METRICS.filter((m) => !m.depth || hasDepth)
    return { score, cols }
  })
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-14 tabular-nums" data-testid="comparison-table">
        <thead>
          <tr>
            <th className="section-label border-b border-[var(--ink-border)] py-2 pr-4 text-left" rowSpan={2}>
              Model
            </th>
            {groups.map(({ score, cols }) => (
              <th
                key={score.reference}
                colSpan={cols.length}
                className="border-b border-l border-[var(--ink-border)] px-3 py-2 text-left text-14 font-semibold text-[var(--ink-text)]"
                data-testid="table-reference"
              >
                Reference: {score.reference_label}
              </th>
            ))}
          </tr>
          <tr>
            {groups.map(({ score, cols }) =>
              cols.map((c, i) => (
                <th
                  key={score.reference + c.key}
                  className={`min-w-[88px] border-b border-[var(--ink-border)] px-3 py-2 text-right align-bottom text-12 font-medium leading-snug text-[var(--ink-text-dim)] ${i === 0 ? 'border-l' : ''}`}
                >
                  {c.label}
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {data.models.map((m) => (
            <tr key={m.key} className="border-b border-[var(--ink-border)]">
              <td className="py-2 pr-4">
                <span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: MODEL_COLOUR[m.model] }} />
                <span className="text-[var(--ink-text)]">{m.solver}</span>
                <div className="text-12 text-[var(--ink-text-faint)]">{m.label}</div>
              </td>
              {groups.map(({ score, cols }) =>
                cols.map((c, i) => {
                  const v = score.rows[m.key]?.[c.key]
                  const best = score.decision?.selected === m.key && c.key === 'iou'
                  return (
                    <td
                      key={score.reference + c.key}
                      className={`px-3 py-2 text-right font-mono ${i === 0 ? 'border-l border-[var(--ink-border)]' : ''}`}
                      style={{ color: best ? 'var(--ink-accent)' : 'var(--ink-text)' }}
                    >
                      {typeof v === 'number' ? c.format(v) : ''}
                    </td>
                  )
                }),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const FIT_COLUMNS: { key: keyof FitRow; label: string; format: (v: number) => string }[] = [
  { key: 'domain_km2', label: 'Shared domain (km²)', format: (v) => v.toFixed(2) },
  { key: 'cell_m', label: 'Cell (m)', format: (v) => v.toFixed(0) },
  { key: 'wet_threshold_m', label: 'Wet threshold (m)', format: (v) => v.toFixed(2) },
  { key: 'matched_times', label: 'Matched output times', format: (v) => v.toFixed(0) },
  { key: 'cells_compared', label: 'Cells compared', format: (v) => v.toLocaleString('en-IN') },
  { key: 'reference_wet_cells', label: 'Reference wet cells', format: (v) => v.toLocaleString('en-IN') },
  { key: 'iou', label: 'IoU (Jaccard)', format: (v) => v.toFixed(2) },
  { key: 'pod', label: 'Probability of Detection', format: (v) => v.toFixed(2) },
  { key: 'far', label: 'False Alarm Ratio', format: (v) => v.toFixed(2) },
  { key: 'depth_rmse_m', label: 'Depth RMSE (m)', format: (v) => v.toFixed(2) },
  { key: 'peak_arrival_difference_s', label: 'Peak arrival difference (h)', format: (v) => (v / 3600).toFixed(3).replace('-', '−') },
]

/**
 * Every model against the reference on its own overlap with it. The domains differ from row to row,
 * so these numbers describe fit and do not rank: the rows keep the order the models are listed in,
 * and the selection is made in the next section, on the domain every model shares.
 */
function FitTable({ data, score }: { data: Payload; score: Score }) {
  const fit = score.fit ?? {}
  const rows = data.models.filter((m) => fit[m.key])
  if (rows.length === 0) return null
  const constrained = rows.filter((m) => {
    const r = fit[m.key]
    return r.particle_spacing_m !== undefined && Math.abs(r.particle_spacing_m - r.model_wet_threshold_m) < 1e-6
  })
  return (
    <div data-testid="fit-table-block">
      <p className="mb-2 text-14 text-[var(--ink-text-dim)]" data-testid="fit-scope">
        Reference: {score.reference_label}. Each model is scored where it and the reference overlap - their shared
        domain, at the coarser cell, at the times both wrote.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-14 tabular-nums" data-testid="fit-table">
          <thead>
            <tr>
              <th className="section-label border-b border-[var(--ink-border)] py-2 pr-4 text-left">Model</th>
              {FIT_COLUMNS.map((c) => (
                <th
                  key={c.key as string}
                  className="min-w-[88px] border-b border-[var(--ink-border)] px-3 py-2 text-right align-bottom text-12 font-medium leading-snug text-[var(--ink-text-dim)]"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const row = fit[m.key]
              return (
                <tr key={m.key} className="border-b border-[var(--ink-border)]" data-testid={`fit-row-${m.key}`}>
                  <td className="py-2 pr-4">
                    <span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: MODEL_COLOUR[m.model] }} />
                    <span className="text-[var(--ink-text)]">{m.solver}</span>
                    <div className="text-12 text-[var(--ink-text-faint)]">{m.label}</div>
                    {row.particle_spacing_m !== undefined && Math.abs(row.particle_spacing_m - row.model_wet_threshold_m) < 1e-6 && (
                      <div className="mt-0.5 text-12 leading-snug text-[var(--ink-text-dim)]" data-testid={`fit-constraint-${m.key}`}>
                        wet threshold = particle spacing {row.particle_spacing_m.toFixed(1)} m
                      </div>
                    )}
                  </td>
                  {FIT_COLUMNS.map((c) => {
                    const v = row[c.key]
                    return (
                      <td key={c.key as string} className="px-3 py-2 text-right font-mono text-[var(--ink-text)]">
                        {typeof v === 'number' ? c.format(v) : ''}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-12 leading-relaxed text-[var(--ink-text-dim)]" data-testid="fit-not-a-ranking">
        This table describes fit, not rank. Each row is measured on a different domain, over a different number of
        cells and times, so the rows are not comparable with one another and are not sorted by any of them. No model is
        selected here: that happens once, in the next section, on the domain every model shares.
        {constrained.map((m) => {
          const r = fit[m.key]
          return (
            <span key={m.key} data-testid={`fit-constraint-note-${m.key}`}>
              {' '}
              {m.solver} cannot resolve water shallower than one particle: at a spacing of {r.particle_spacing_m?.toFixed(1)} m
              its wet threshold is {r.model_wet_threshold_m.toFixed(1)} m against the reference's{' '}
              {r.reference_wet_threshold_m.toFixed(2)} m. That is a modelling constraint, not a setting, and it raises the
              false alarm ratio of its row against a reference that resolves thinner water.
            </span>
          )
        })}
      </p>
    </div>
  )
}

function Methods({ data }: { data: Payload }) {
  return (
    <div className="mt-3 space-y-1 text-12 leading-relaxed text-[var(--ink-text-dim)]" data-testid="comparison-methods">
      {data.scores.map((s) =>
        s.grid ? (
          <p key={s.reference}>
            Reference: {s.reference_label}
            {s.source ? ` (${s.source})` : ''}. Scored over the {s.grid.compared_km2.toFixed(2)} km² ({s.grid.compared_cells} cells at{' '}
            {s.grid.cell_m.toFixed(0)} m) common to every model, wet at peak depth ≥ {s.grid.wet_threshold_m.toFixed(2)} m
            {s.grid.matched_times !== undefined && s.grid.window_s
              ? `, ${s.grid.matched_times} matched output times from ${duration(s.grid.window_s[0])} to ${duration(s.grid.window_s[1])} after breach`
              : ''}
            . Excluded: {s.grid.excluded_cells} cells{s.grid.exclusion_rule ? ` (${s.grid.exclusion_rule})` : ''}.
            {s.bootstrap ? ` Interval: ${s.bootstrap.draws} paired bootstrap draws of ${s.bootstrap.resampled} (${s.bootstrap.blocks} blocks).` : ''}
          </p>
        ) : null,
      )}
      {data.scores.some((s) => Object.values(s.rows).some((r) => r.far === undefined || r.depth_rmse_m === undefined)) && (
        <p>
          A blank cell is a metric the inputs cannot give: FAR needs at least one cell the model shows as wet; depth
          errors and the arrival difference need cells wet in both the model and the reference.
        </p>
      )}
      {data.scores.flatMap((s) => s.skipped).map((k) => (
        <p key={k.key}>
          {labelOf(data, k.key)} not scored: {k.reason}.
        </p>
      ))}
      {data.absent_models.map((m) => (
        <p key={m.model}>
          {m.solver} not scored: {m.reason}.
        </p>
      ))}
    </div>
  )
}

/** The size of the evidence behind a result: shown next to every result, whatever it is. */
function Sample({ score }: { score: Score }) {
  const d = score.decision
  if (!d || !score.grid) return null
  return (
    <p className="mt-1 font-mono text-12 tabular-nums text-[var(--ink-text-dim)]" data-testid="comparison-sample">
      {score.grid.compared_cells.toLocaleString('en-IN')} cells compared · {d.reference_wet_cells.toLocaleString('en-IN')} reference wet cells
      (a result needs at least {d.min_reference_wet_cells})
    </p>
  )
}

function InsufficientSample({ score }: { score: Score }) {
  const d = score.decision!
  return (
    <div data-testid="insufficient-sample">
      <p className="text-16 font-semibold text-[var(--ink-text)]">Not supported by enough data. No model is selected.</p>
      <p className="mt-1 text-14 text-[var(--ink-text-dim)]">
        The reference has only {d.reference_wet_cells.toLocaleString('en-IN')} wet cells inside the compared domain, fewer than the{' '}
        {d.min_reference_wet_cells} a result needs. The sample is too small to separate the models at this site, whatever the confidence
        interval says.
      </p>
      <Sample score={score} />
    </div>
  )
}

function Decision({ data, score }: { data: Payload; score: Score }) {
  const d = score.decision
  if (!d) return null
  if (d.outcome === 'insufficient_sample') return <InsufficientSample score={score} />
  if (d.compared.length < 2 || !d.ci95) {
    return (
      <div>
        <p className="text-14 text-[var(--ink-text-dim)]">{d.reason ?? 'Fewer than two models could be scored.'}</p>
        <Sample score={score} />
      </div>
    )
  }
  const [a, b] = d.compared
  const ia = score.rows[a]?.iou
  const ib = score.rows[b]?.iou
  const ci = `${d.ci95[0].toFixed(2)} to ${d.ci95[1].toFixed(2)}`
  if (!d.separates || !d.selected) {
    return (
      <div data-testid="no-winner">
        <p className="text-16 font-semibold text-[var(--ink-text)]">The comparison does not separate these two models at this site.</p>
        <p className="mt-1 text-14 text-[var(--ink-text-dim)]">
          {labelOf(data, a)} IoU {ia?.toFixed(2)}, {labelOf(data, b)} IoU {ib?.toFixed(2)} against {score.reference_label}; the 95 % interval
          of the difference ({ci}) includes zero. No model is declared the winner.
        </p>
        <Sample score={score} />
      </div>
    )
  }
  return (
    <div data-testid="winner">
      <p className="text-16 font-semibold text-[var(--ink-text)]">
        {labelOf(data, d.selected)} agrees better with {score.reference_label}.
      </p>
      <p className="mt-1 text-14 text-[var(--ink-text-dim)]">
        IoU {ia?.toFixed(2)} against {ib?.toFixed(2)} for {labelOf(data, b)}; difference {d.iou_difference?.toFixed(2)}, 95 % interval {ci}.
      </p>
      <Sample score={score} />
    </div>
  )
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="grid grid-cols-[32px_minmax(0,1fr)] gap-3 border-t border-[var(--ink-border)] py-3 first:border-t-0">
      <span className="font-mono text-16 tabular-nums text-[var(--ink-text-faint)]">{n}</span>
      <div>
        <div className="section-label mb-1">{title}</div>
        <div className="text-14 leading-relaxed text-[var(--ink-text)]">{children}</div>
      </div>
    </li>
  )
}

function DecisionTrace({ data, score }: { data: Payload; score: Score }) {
  const ref = data.references.find((r) => r.kind === score.reference && r.available)
  const d = score.decision
  const weights = Object.entries(data.weights)
  const total = weights.reduce((s, [, w]) => s + w, 0)
  const scored = weights.filter(([, w]) => w > 0)
  const reported = weights.filter(([, w]) => w === 0)
  const composite = (key: string): number | null => {
    const row = score.rows[key]
    if (!row || !total) return null
    let sum = 0
    for (const [m, w] of scored) {
      const v = row[m as keyof Row]
      if (typeof v !== 'number') return null
      sum += w * v
    }
    return sum / total
  }
  const models = data.models.filter((m) => score.rows[m.key])
  const ranked = d?.compared ?? []
  const fmt = (v: number | null | undefined, digits = 2) => (typeof v === 'number' ? v.toFixed(digits) : '')
  return (
    <ol className="list-none" data-testid="decision-trace">
      <Step n={1} title="Reference">
        <div>{score.reference_label}</div>
        {ref?.why && <div className="text-[var(--ink-text-dim)]">Why: {ref.why}.</div>}
        {score.source && <div className="font-mono text-12 text-[var(--ink-text-dim)]">{score.source}</div>}
      </Step>
      {score.grid && (
        <Step n={2} title="Domain">
          <div className="tabular-nums">
            {score.grid.compared_cells.toLocaleString('en-IN')} cells compared ({score.grid.compared_km2.toFixed(2)} km² at{' '}
            {score.grid.cell_m.toFixed(0)} m), the area every model and the reference share; wet at depth ≥{' '}
            {score.grid.wet_threshold_m.toFixed(2)} m.
          </div>
          {d && (
            <div className="tabular-nums">
              {d.reference_wet_cells.toLocaleString('en-IN')} of them wet in the reference (a result needs at least {d.min_reference_wet_cells}).
            </div>
          )}
          <div className="tabular-nums text-[var(--ink-text-dim)]">
            {score.grid.excluded_cells.toLocaleString('en-IN')} void cells excluded
            {score.grid.exclusion_rule ? `: ${score.grid.exclusion_rule}` : ''}.
          </div>
        </Step>
      )}
      <Step n={3} title="Metrics and weights">
        <div className="tabular-nums">
          Scored: {scored.map(([m, w]) => `${WEIGHT_LABEL[m] ?? m} × ${w.toFixed(1)}`).join(', ')}.
        </div>
        <div className="text-[var(--ink-text-dim)]">
          Reported, weight 0: {reported.map(([m]) => WEIGHT_LABEL[m] ?? m).join(', ')}.
        </div>
      </Step>
      <Step n={4} title="Scores">
        <table className="tabular-nums">
          <tbody>
            {models.map((m) => {
              const c = composite(m.key)
              return (
                <tr key={m.key}>
                  <td className="pr-4">{m.solver}</td>
                  <td className="font-mono text-[var(--ink-text-dim)]">
                    {c === null
                      ? 'no score: a scored metric is undefined for this model'
                      : `${scored.map(([k, w]) => `${w.toFixed(1)} × ${fmt(score.rows[m.key][k as keyof Row] as number)}`).join(' + ')}${total !== 1 ? ` ÷ ${total.toFixed(1)}` : ''} = ${fmt(c)}`}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Step>
      <Step n={5} title="Margin">
        {ranked.length >= 2 && d?.iou_difference !== null && d?.iou_difference !== undefined ? (
          <span className="font-mono tabular-nums">
            {labelOf(data, ranked[0])} − {labelOf(data, ranked[1])} = {fmt(composite(ranked[0]))} − {fmt(composite(ranked[1]))} ={' '}
            {fmt(d.iou_difference)}
          </span>
        ) : (
          <span className="text-[var(--ink-text-dim)]">{d?.reason ?? 'Fewer than two models were scored.'}</span>
        )}
      </Step>
      <Step n={6} title="Uncertainty">
        {d?.ci95 && score.bootstrap ? (
          <span className="tabular-nums">
            95 % interval of the margin: <span className="font-mono">{fmt(d.ci95[0])} to {fmt(d.ci95[1])}</span>, from{' '}
            {score.bootstrap.draws.toLocaleString('en-IN')} paired bootstrap draws of {score.bootstrap.resampled} (
            {score.bootstrap.blocks} blocks).
          </span>
        ) : (
          <span className="text-[var(--ink-text-dim)]">No interval: the margin needs two scored models.</span>
        )}
      </Step>
      <Step n={7} title="Decision rule">
        <span data-testid="decision-rule">{data.decision_rule}</span>
      </Step>
      <Step n={8} title="Outcome">
        {d?.outcome === 'insufficient_sample' ? (
          <span className="font-semibold" data-testid="no-winner">
            No model is selected: not supported by enough data. The reference has {d.reference_wet_cells} wet cells in the compared domain,
            fewer than {d.min_reference_wet_cells}, so the sample is too small to separate the models at this site
            {d.ci95 ? `, although the interval (${fmt(d.ci95[0])} to ${fmt(d.ci95[1])}) excludes zero` : ''}.
          </span>
        ) : d?.selected && d.separates ? (
          <span className="font-semibold" data-testid="winner">
            {labelOf(data, d.selected)} is selected: the interval ({fmt(d.ci95?.[0])} to {fmt(d.ci95?.[1])}) excludes zero.
          </span>
        ) : (
          <span className="font-semibold" data-testid="no-winner">
            No model is selected. The comparison does not separate these models at this site
            {d?.ci95 ? `: the interval (${fmt(d.ci95[0])} to ${fmt(d.ci95[1])}) includes zero` : ''}.
          </span>
        )}
        <Sample score={score} />
      </Step>
    </ol>
  )
}

const CLASS_COLOURS: [number, string, string][] = [
  [2, C.extentAgree, 'Both wet (agreement)'],
  [3, C.extentOver, 'Model wet, reference dry (over-prediction)'],
  [4, C.extentMissed, 'Reference wet, model dry (missed)'],
]

function classImage(width: number, height: number, b64: string): string {
  const bin = atob(b64)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  const img = ctx.createImageData(width, height)
  const rgb: Record<number, [number, number, number, number]> = {}
  for (const [cls, hex] of CLASS_COLOURS) {
    const h = hex.replace('#', '')
    rgb[cls] = [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16), 235]
  }
  for (let i = 0; i < bin.length; i += 1) {
    const cls = bin.charCodeAt(i)
    // both dry: a faint veil so the compared cells read as compared; outside and excluded stay clear
    const c = rgb[cls] ?? (cls === 1 ? [10, 12, 16, 90] : [0, 0, 0, 0])
    img.data.set(c, i * 4)
  }
  ctx.putImageData(img, 0, 0)
  return canvas.toDataURL('image/png')
}

function AgreementMap({ score, model }: { score: Score; model: ModelInfo }) {
  const cls = score.classes!
  const row = score.rows[model.key]
  const url = useMemo(() => classImage(cls.width, cls.height, cls.by_model_u8[model.key]), [cls, model.key])
  const corners = cls.corners_lonlat
  const bounds = boundsOf([{ type: 'MultiPoint', coordinates: corners }])!
  const sources: Record<string, SourceSpecification> = useMemo(
    () => ({ classes: { type: 'image', url, coordinates: corners as never } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url],
  )
  const layers: LayerSpecification[] = useMemo(
    () => [{ id: 'classes', type: 'raster', source: 'classes', paint: { 'raster-resampling': 'nearest', 'raster-fade-duration': 0 } }],
    [],
  )
  return (
    <figure className="min-w-0" data-testid="agreement-map">
      <figcaption className="mb-1 flex items-baseline justify-between gap-2">
        <span className="flex items-center gap-2 text-14">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: MODEL_COLOUR[model.model] }} />
          {model.solver}
        </span>
        <span className="font-mono text-12 tabular-nums text-[var(--ink-text-dim)]">
          {row.tp} agree · {row.fp} over · {row.fn} missed
        </span>
      </figcaption>
      <div className="relative h-[280px] overflow-hidden rounded-[var(--r-control)]">
        <BaseMap bounds={bounds} sources={sources} layers={layers} padding={24} />
      </div>
    </figure>
  )
}

function IouChart({ data, score }: { data: Payload; score: Score }) {
  const s = score.iou_series!
  const models = data.models.filter((m) => s.by_model[m.key])
  const [hover, setHover] = useState<number | null>(null)
  const W = 640
  const H = 220
  const pad = { l: 44, r: 120, t: 12, b: 32 }
  const t0 = s.times_s[0]
  const t1 = s.times_s[s.times_s.length - 1]
  const x = (t: number) => pad.l + ((t - t0) / Math.max(1, t1 - t0)) * (W - pad.l - pad.r)
  const y = (v: number) => pad.t + (1 - v) * (H - pad.t - pad.b)
  const paths = models.map((m) => {
    let d = ''
    let open = false
    s.by_model[m.key].forEach((v, i) => {
      if (v === null) {
        open = false
        return
      }
      d += `${open ? 'L' : 'M'}${x(s.times_s[i]).toFixed(1)},${y(v).toFixed(1)}`
      open = true
    })
    return { m, d }
  })
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - box.left) / box.width) * W
    let best = 0
    s.times_s.forEach((t, i) => {
      if (Math.abs(x(t) - px) < Math.abs(x(s.times_s[best]) - px)) best = i
    })
    setHover(best)
  }
  return (
    <figure className="max-w-[720px]" data-testid="iou-chart">
      <figcaption className="section-label mb-2">IoU at each matched time step</figcaption>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-12 text-[var(--ink-text-dim)]">
        {models.map((m) => (
          <span key={m.key} className="flex items-center gap-2">
            <span className="inline-block h-0.5 w-4" style={{ background: MODEL_COLOUR[m.model] }} />
            {m.solver}
          </span>
        ))}
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="IoU against time for each model" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          {[0, 0.25, 0.5, 0.75, 1].map((v) => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke="var(--ink-grid)" strokeWidth={1} />
              <text x={pad.l - 8} y={y(v) + 4} textAnchor="end" fill="var(--ink-text-faint)" style={{ fontSize: 'var(--fs-11)' }}>
                {v.toFixed(2)}
              </text>
            </g>
          ))}
          {s.times_s.map((t, i) => (
            <text key={i} x={x(t)} y={H - 10} textAnchor="middle" fill="var(--ink-text-faint)" style={{ fontSize: 'var(--fs-11)' }}>
              {duration(t)}
            </text>
          ))}
          {hover !== null && <line x1={x(s.times_s[hover])} x2={x(s.times_s[hover])} y1={pad.t} y2={H - pad.b} stroke="var(--ink-border-strong)" strokeWidth={1} />}
          {paths.map(({ m, d }) => (
            <path key={m.key} d={d} fill="none" stroke={MODEL_COLOUR[m.model]} strokeWidth={2} strokeLinejoin="round" />
          ))}
          {models.map((m) =>
            s.by_model[m.key].map((v, i) =>
              v === null ? null : (
                <circle key={`${m.key}${i}`} cx={x(s.times_s[i])} cy={y(v)} r={4} fill={MODEL_COLOUR[m.model]} stroke="var(--ink-surface)" strokeWidth={2} />
              ),
            ),
          )}
          {models.map((m) => {
            const vals = s.by_model[m.key]
            const last = vals.map((v, i) => [v, i] as const).filter(([v]) => v !== null).pop()
            return last ? (
              <text key={m.key} x={x(s.times_s[last[1]]) + 10} y={y(last[0]!) + 4} fill="var(--ink-text-dim)" style={{ fontSize: 'var(--fs-12)' }}>
                {m.solver}
              </text>
            ) : null
          })}
        </svg>
        {hover !== null && (
          <div className="overlay pointer-events-none absolute top-2 px-3 py-2 text-12 tabular-nums" style={{ left: `${(x(s.times_s[hover]) / W) * 100}%`, transform: 'translateX(8px)' }}>
            <div className="text-[var(--ink-text-dim)]">{duration(s.times_s[hover])} after breach</div>
            {models.map((m) => (
              <div key={m.key} className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: MODEL_COLOUR[m.model] }} />
                <span>{m.solver}</span>
                <span className="ml-auto font-mono">{s.by_model[m.key][hover] === null ? 'no wet cell' : s.by_model[m.key][hover]!.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <details className="mt-2 text-12 text-[var(--ink-text-dim)]">
        <summary className="cursor-pointer">Values</summary>
        <table className="mt-2 tabular-nums">
          <thead>
            <tr>
              <th className="pr-4 text-left font-medium">After breach</th>
              {models.map((m) => (
                <th key={m.key} className="pr-4 text-right font-medium">
                  {m.solver}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {s.times_s.map((t, i) => (
              <tr key={i}>
                <td className="pr-4">{duration(t)}</td>
                {models.map((m) => (
                  <td key={m.key} className="pr-4 text-right font-mono">
                    {s.by_model[m.key][i] === null ? 'no wet cell' : s.by_model[m.key][i]!.toFixed(2)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-1">A step where neither the model nor the reference has a wet cell has no IoU, and the line breaks there.</p>
      </details>
    </figure>
  )
}

function Agreement({ data, score }: { data: Payload; score: Score }) {
  const models = data.models.filter((m) => score.classes?.by_model_u8[m.key])
  if (!score.classes || !models.length) return null
  return (
    <div className="grid gap-6">
      <div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {models.map((m) => (
            <AgreementMap key={m.key} score={score} model={m} />
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12 text-[var(--ink-text-dim)]" data-testid="agreement-legend">
          {CLASS_COLOURS.map(([cls, colour, label]) => (
            <span key={cls} className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-[2px]" style={{ background: colour }} />
              {label}
            </span>
          ))}
          <span>Against {score.reference_label}, at peak depth over the matched time steps.</span>
        </div>
      </div>
      {score.iou_series && <IouChart data={data} score={score} />}
    </div>
  )
}

function SelectedModel({ data, run }: { data: Payload; run: Run }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const ownKey = `run:${run.id}`
  const choices = [
    { key: null as string | null, label: `This run's own result (${run.solver})` },
    ...data.models.filter((m) => m.key !== ownKey).map((m) => ({ key: m.key as string | null, label: m.label })),
  ]
  const save = useMutation({
    mutationFn: (key: string | null) => api.selectModel(run.id, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compare-models', run.id] })
      queryClient.invalidateQueries({ queryKey: ['runs'] })
    },
  })
  const current = data.selected_model
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
      <div className="space-y-4">
        {data.scores.length === 0 ? (
          <p className="text-14 text-[var(--ink-text-dim)]" data-testid="no-reference">
            No reference extent is available at this site, so no model is scored. A HEC-RAS 2-D plan HDF, or an observed
            extent at {data.references.find((r) => r.kind === 'observed_sar')?.expected_path}, is what the comparison needs.
          </p>
        ) : (
          data.scores.map((s) => <Decision key={s.reference} data={data} score={s} />)
        )}
        {data.scores.some((s) => !s.decision?.selected) && data.scores.length > 0 && (
          <p className="text-14 text-[var(--ink-text-dim)]">
            With no model selected by the comparison, the model the other pages show is the one chosen here.
          </p>
        )}
      </div>
      <div className="rounded-[var(--r-panel)] border border-[var(--ink-border)] bg-[var(--ink-surface-raised)] p-4" data-testid="selected-model-card">
        <div className="section-label mb-2">Selected model · shown on 3D View and Impact</div>
        <div className="flex flex-col gap-2">
          {choices.map((c) => {
            const active = (current ?? null) === c.key
            return (
              <button
                key={c.key ?? 'own'}
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate(c.key)}
                className="ctl flex h-10 items-center justify-between !px-3 text-left !text-14"
                style={active ? { borderColor: 'var(--ink-accent)', color: 'var(--ink-text)' } : undefined}
                aria-pressed={active}
              >
                <span className="truncate">{c.label}</span>
                {active && <span className="section-label" style={{ color: 'var(--ink-accent)' }}>selected</span>}
              </button>
            )
          })}
        </div>
        {save.isError && <p className="mt-2 text-12" style={{ color: 'var(--ink-alert-text)' }}>{String(save.error)}</p>}
        <div className="mt-3 flex gap-2">
          <button type="button" className="ctl h-10 !px-3 !text-14" onClick={() => navigate('/particles')}>
            Open 3D View
          </button>
          <button type="button" className="ctl h-10 !px-3 !text-14" onClick={() => navigate('/impact')}>
            Open Impact
          </button>
        </div>
      </div>
    </div>
  )
}

function WhereMap({ data }: { data: Payload }) {
  const ref = data.references.find((r) => r.available && r.footprint_lonlat)
  const domain = data.scores.find((s) => s.grid)?.grid?.compared_domain_lonlat
  const models = data.models.filter((m) => m.footprint_lonlat)
  const bounds = boundsOf([ref?.footprint_lonlat, domain, ...models.map((m) => m.footprint_lonlat)])
  const style = useMemo(() => {
    const sources: Record<string, SourceSpecification> = {}
    const layers: LayerSpecification[] = []
    if (ref?.footprint_lonlat) {
      sources.ref = { type: 'geojson', data: ref.footprint_lonlat as GeoJSON.Geometry }
      layers.push({ id: 'ref-fill', type: 'fill', source: 'ref', paint: { 'fill-color': C.modelReference, 'fill-opacity': 0.18 } })
      layers.push({ id: 'ref-line', type: 'line', source: 'ref', paint: { 'line-color': C.modelReference, 'line-width': 1.5 } })
    }
    models.forEach((m) => {
      sources[m.key] = { type: 'geojson', data: m.footprint_lonlat as GeoJSON.Geometry }
      layers.push({ id: `${m.key}-line`, type: 'line', source: m.key, paint: { 'line-color': MODEL_COLOUR[m.model], 'line-width': 2 } })
    })
    if (domain) {
      sources.domain = { type: 'geojson', data: domain as GeoJSON.Geometry }
      layers.push({ id: 'domain-line', type: 'line', source: 'domain', paint: { 'line-color': C.accent, 'line-width': 2, 'line-dasharray': [2, 1.5] } })
    }
    return { sources, layers }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.run_id])
  if (!bounds) return null
  return (
    <div>
      <div className="relative overflow-hidden rounded-[var(--r-control)]" style={{ height: 'max(52vh, 420px)' }} data-testid="comparison-map">
        <BaseMap bounds={bounds} runId={data.run_id} sources={style.sources} layers={style.layers} padding={32} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12 text-[var(--ink-text-dim)]">
        {ref && (
          <span className="flex items-center gap-2">
            <span className="inline-block h-2 w-4" style={{ background: C.modelReference, opacity: 0.6 }} />
            {ref.label}
          </span>
        )}
        {models.map((m) => (
          <span key={m.key} className="flex items-center gap-2">
            <span className="inline-block h-0.5 w-4" style={{ background: MODEL_COLOUR[m.model] }} />
            {m.solver} peak extent
          </span>
        ))}
        {domain && (
          <span className="flex items-center gap-2">
            <span className="inline-block h-0.5 w-4" style={{ background: C.accent }} />
            compared domain
          </span>
        )}
      </div>
    </div>
  )
}

export default function Comparison() {
  const activeSiteId = useAppStore((s) => s.activeSiteId)
  const setActiveSiteId = useAppStore((s) => s.setActiveSiteId)
  const selectedRunId = useAppStore((s) => s.selectedRunId)
  const setSelectedRunId = useAppStore((s) => s.setSelectedRunId)
  const runs = useQuery<Run[]>({ queryKey: ['runs'], queryFn: api.runs })
  const sites = useQuery<{ sites: { id: string; display_name: string }[] }>({ queryKey: ['sites'], queryFn: () => api.raw('/sites') })
  const done = useMemo(
    () => (runs.data ?? []).filter((r) => r.status === 'done' && r.site_id === activeSiteId && r.solver !== 'dualsphysics'),
    [runs.data, activeSiteId],
  )
  // Only a site with a finished run of its own: a case with nothing to compare offers no row.
  const siteOptions = useMemo(() => {
    const withRuns = new Set((runs.data ?? []).filter((r) => r.status === 'done' && r.site_id).map((r) => r.site_id as string))
    return (sites.data?.sites ?? []).filter((s) => withRuns.has(s.id))
  }, [sites.data, runs.data])
  const [runId, setRunId] = useState<string | null>(null)
  useEffect(() => {
    if (runId && done.some((r) => r.id === runId)) return
    const pick = done.find((r) => r.id === selectedRunId) ?? done[0]
    setRunId(pick?.id ?? null)
  }, [done, runId, selectedRunId])
  const run = done.find((r) => r.id === runId) ?? null

  const query = useQuery<Payload>({
    queryKey: ['compare-models', runId],
    queryFn: () => api.raw(`/compare/models?run_id=${encodeURIComponent(runId!)}`),
    enabled: Boolean(runId),
    staleTime: 60_000,
  })
  const data = query.data

  return (
    <div className="h-full overflow-y-auto bg-[var(--ink-bg)] text-[var(--ink-text)]" data-testid="comparison-page">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-4 p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="page-title">Comparison</h1>
          {siteOptions.length > 1 && (
            <label className="flex items-center gap-2 text-14 text-[var(--ink-text-dim)]">
              Case
              <select
                className="ctl h-10 !text-14"
                value={activeSiteId}
                onChange={(e) => {
                  setActiveSiteId(e.target.value)
                  setRunId(null)
                }}
                data-testid="comparison-case"
              >
                {siteOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.display_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {done.length > 0 && (
            <label className="flex items-center gap-2 text-14 text-[var(--ink-text-dim)]">
              Run
              <select
                className="ctl h-10 !text-14"
                value={runId ?? ''}
                onChange={(e) => {
                  setRunId(e.target.value)
                  setSelectedRunId(e.target.value)
                }}
              >
                {done.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.id} · {r.solver}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {query.isPending && runId && <div className="skeleton h-48" />}
        {query.isError && <p className="text-14" style={{ color: 'var(--ink-alert-text)' }}>{String(query.error)}</p>}
        {data && run && (
          <>
            {([
              data.scores.some((s) => s.fit && Object.keys(s.fit).length > 0) && {
                title: 'Fit against the reference, model by model',
                testid: 'comparison-fit',
                body: data.scores.map((s) => <FitTable key={`fit-${s.reference}`} data={data} score={s} />),
              },
              data.scores.length > 0 && {
                title: 'Scores on the domain every model shares',
                testid: 'comparison-scores',
                body: (
                  <>
                    <ScoreTable data={data} />
                    <Methods data={data} />
                  </>
                ),
              },
              data.scores.length > 0 && {
                title: 'How the selection was made',
                testid: 'comparison-trace',
                body: data.scores.map((s) => <DecisionTrace key={`trace-${s.reference}`} data={data} score={s} />),
              },
              data.scores.some((s) => s.classes) && {
                title: 'Extent agreement',
                testid: 'comparison-agreement',
                body: data.scores.map((s) => <Agreement key={s.reference} data={data} score={s} />),
              },
              {
                title: 'Selected model',
                testid: 'comparison-selected',
                body: (
                  <>
                    <SelectedModel data={data} run={run} />
                    {data.scores.length === 0 && <Methods data={data} />}
                  </>
                ),
              },
              data.scores.length > 0 && {
                title: 'Where the models were compared',
                testid: 'comparison-where',
                body: <WhereMap data={data} />,
              },
            ] as (false | { title: string; testid: string; body: React.ReactNode })[])
              .filter((s): s is { title: string; testid: string; body: React.ReactNode } => Boolean(s))
              .map((s, i) => (
                <Section key={s.testid} n={i + 1} title={s.title} testid={s.testid}>
                  {s.body}
                </Section>
              ))}
          </>
        )}
      </div>
    </div>
  )
}
